import { useCallback, useEffect } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { ViewType, SeriesCriteriaCategory } from 'generated/graphql';
import {
  hpFormStepConfigurations,
  hangingProtocolStepIndex,
  hpFormStepsBaseline,
  initialHPFormStateSnapshot,
} from '../state';
import { useClinicalStepWriters } from '../clinicalStepStore';
import { hangingProtocolLayoutsState } from '../../ViewportsConfigurations/state';
import { layoutsForAllCasesState } from '../../Viewer/StudyLoader/viewerLoaderState';
import { useViewerLayoutWriters } from '../../Viewer/StudyLoader/viewerLayoutWriters';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import type { StepConfiguration, ViewportConfigurationEntry } from '../steppedHPTypes';
import type { HangingProtocolFormState } from '../../Viewer/StudiesBar/HangingProtocols/HPv4/hangingProtocolFormHelpers';
import { classifierInputSignature } from '../../Viewer/StudiesBar/HangingProtocols/HPv4/hangingProtocolFormHelpers';
import type { Layouts } from '../../Viewer/StudyLoader/viewerLoaderState';
import type { DehydratedViewportsConfigurations } from '../../ViewportsConfigurations/types';
import type { RecoilValue, SetterOrUpdater } from 'recoil';
import { generateViewportId } from '../../Viewer/viewerUtils';
import {
  parseLayoutString,
  formatLayoutString,
  isViewportInLayoutBounds,
  toAllowedViewportType,
  fromAllowedViewportType,
} from '../steppedHPTypes';
//
// When the HP form is open, step navigation (StepBar clicks, keyboard shortcuts)
// must save the current form state before switching and load the new step's data.
// This module-level ref allows useStepFormSync to register its `switchToStep`
// function so that useHPStepOperations navigation functions can route through it.

const stepSwitchInterceptorRef: { current: ((index: number) => void) | null } = { current: null };

export function getStepSwitchInterceptor(): ((index: number) => void) | null {
  return stepSwitchInterceptorRef.current;
}

//
// When the HP form is open, addStep/deleteStep need to flush the current
// form state into hpFormStepConfigurations before reading step data.
// This module-level ref allows useStepFormSync to register its
// `flushCurrentStepToConfigurations` function so that useHPStepOperations
// can invoke it before cloning or snapshotting steps.

const formFlushInterceptorRef: { current: (() => StepConfiguration[] | null) | null } = {
  current: null,
};

export function getFormFlushInterceptor(): (() => StepConfiguration[] | null) | null {
  return formFlushInterceptorRef.current;
}

/**
 * Convert a form viewport entry to a step viewport configuration entry.
 */
export function formViewportToStepViewport(
  formViewport: HangingProtocolFormState['viewports'][string]
): ViewportConfigurationEntry {
  return {
    display_name: formViewport.seriesCriteriaSourceText ?? '',
    study_smid: null,
    series_smid: null,
    view_type: toAllowedViewportType(formViewport.viewType),
    series_criteria_source_text: formViewport.seriesCriteriaSourceText,
    comparison_index: formViewport.comparisonIndex ?? null,
    ...(formViewport.seriesClassificationCriteria?.length
      ? {
          criteria: formViewport.seriesClassificationCriteria.map(({ category, value }) => ({
            category,
            value,
          })),
        }
      : {}),
    is_clear: formViewport.isClearViewport ?? false,
  };
}

/**
 * Convert a step viewport configuration entry to form viewport format.
 */
export function stepViewportToFormViewport(
  entry: ViewportConfigurationEntry,
  /** The protocol's study description as it stands now, for the signature below. */
  studyDescription: string = ''
): HangingProtocolFormState['viewports'][string] {
  // Infer cleared state: explicit is_clear flag takes precedence, otherwise
  // treat viewports with no study, no series, AND no criteria as cleared.
  // Criteria-based viewports intentionally have null study/series (they match
  // by classification), so we must not mark them as cleared.
  const hasCriteria = Boolean(entry.criteria?.length || entry.series_criteria_source_text);
  const isClearViewport =
    entry.is_clear ?? (entry.study_smid == null && entry.series_smid == null && !hasCriteria);

  const comparisonIndex = entry.comparison_index ?? null;

  return {
    seriesCriteriaSourceText: entry.series_criteria_source_text ?? '',
    comparisonIndex,
    isClearViewport,
    viewType: fromAllowedViewportType(entry.view_type),
    // The one seeding path that supplies criteria, so it is the one that has to say what they
    // describe. A stored viewport with a description but no criteria records nothing, which reads as
    // still-waiting and has to be classified again before it can be re-saved -- that is the VX-4977
    // state itself.
    ...(entry.criteria?.length
      ? {
          criteriaSignature: classifierInputSignature(
            entry.series_criteria_source_text ?? '',
            // A stored step does not carry the study description its criteria were derived from, so
            // this takes the one the protocol has now. The two agree unless the reader edits it,
            // which is precisely when re-classifying is the right answer. Passing '' instead would
            // leave every current-study viewport waiting on the classifier just to open the form,
            // so a rename would need SSC to be up.
            comparisonIndex === null ? studyDescription : ''
          ),
        }
      : {}),
    ...(entry.criteria?.length
      ? {
          seriesClassificationCriteria: entry.criteria.map(({ category, value }) => ({
            category: category as SeriesCriteriaCategory,
            value,
            displayName: '',
          })),
        }
      : {}),
  };
}

/**
 * Projects a step onto the form's flat `viewports` map: an entry per viewport the step configures,
 * plus a cleared placeholder for every slot of the step's layout it does not, which is what restores
 * the "clear viewport" state after a save or reload.
 *
 * Extracted so the same projection can produce both sides of the form's unsaved-changes comparison --
 * the arriving step's live entries and the baseline entries they are diffed against. Two projections
 * of the same step data that disagree about, say, which placeholders to emit read as a user edit
 * (VX-4947), so the fix depends on there being exactly one of them.
 */
function stepToFormViewports(
  step: StepConfiguration,
  studyDescription: string = ''
): HangingProtocolFormState['viewports'] {
  const viewports: HangingProtocolFormState['viewports'] = {};

  for (const [viewportId, entry] of Object.entries(step.viewports)) {
    viewports[viewportId] = stepViewportToFormViewport(entry, studyDescription);
  }

  for (const [windowId, layoutStr] of Object.entries(step.layouts)) {
    const [rows, cols] = parseLayoutString(layoutStr);
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const viewportId = generateViewportId(windowId, row, col);
        viewports[viewportId] ??= {
          seriesCriteriaSourceText: '',
          comparisonIndex: null,
          isClearViewport: true,
          viewType: ViewType.TwoDDre,
        };
      }
    }
  }

  return viewports;
}

/**
 * Convert viewer Layouts ({ "0": [2, 2] }) to step layouts format ({ "0": "2,2" }).
 */
export function layoutsToStepLayouts(layouts: Layouts): Record<string, string> {
  return Object.fromEntries(
    Object.entries(layouts).map(([id, layout]) => [id, formatLayoutString(layout)])
  );
}

/**
 * Convert step layouts ({ "0": "2,2" }) to viewer Layouts format ({ "0": [2, 2] }).
 */
export function stepLayoutsToLayouts(stepLayouts: Record<string, string>): Layouts {
  return Object.fromEntries(
    Object.entries(stepLayouts).map(([id, layoutStr]) => [id, parseLayoutString(layoutStr)])
  );
}

/**
 * Build a DehydratedViewportsConfigurations from a step's viewports.
 * This allows the viewer to visually update when switching steps in edit mode.
 */
export function stepViewportConfigsToDehydrated(
  viewportConfigs: Record<string, ViewportConfigurationEntry>
): DehydratedViewportsConfigurations {
  return Object.fromEntries(
    Object.entries(viewportConfigs).map(([id, config]) => [
      id,
      {
        viewType: fromAllowedViewportType(config.view_type),
        study: config.study_smid ? { smid: config.study_smid } : null,
        series: config.series_smid ? { smid: config.series_smid } : null,
        stack: config.stack_smid ? { smid: config.stack_smid } : null,
        comparisonIndex: config.comparison_index ?? null,
        seriesDescriptions: [config.display_name],
      },
    ])
  );
}

type UseStepFormSyncArgs = {
  formStateAtom: RecoilValue<HangingProtocolFormState>;
  setFormState: SetterOrUpdater<HangingProtocolFormState>;
};

type UseStepFormSyncReturn = {
  switchToStep: (newIndex: number) => void;
  flushCurrentStepToConfigurations: () => StepConfiguration[] | null;
};

/**
 * Bridges the flat form state with per-step configurations.
 *
 * When switching steps in edit mode:
 * 1. Saves the current form viewports + layouts into the departing step's StepConfiguration
 * 2. Loads the arriving step's viewports + layouts into the form state and viewer
 *
 * Call `flushCurrentStepToConfigurations()` before form submission to persist the active step.
 */
export const useStepFormSync = ({
  formStateAtom,
  setFormState,
}: UseStepFormSyncArgs): UseStepFormSyncReturn => {
  const stepConfigurations = useRecoilValue(hpFormStepConfigurations);
  const currentStepIndex = useRecoilValue(hangingProtocolStepIndex);
  // The index is owned by clinicalStepStore; this hook reads it and asks the store to move it.
  //
  // Position only, no identity, because this hook *is* the form's step switch -- the registered
  // interceptor every real step click and keyboard shortcut goes through while the form is open. The
  // number it moves to is a slot in `hpFormStepConfigurations`, which can diverge from the viewer's
  // list, so letting the selector resolve a `stepSmid` from `steppedHPConfigurations` would stamp
  // whichever viewer step happens to sit at that number. Saving the form then publishes the form's
  // steps into the viewer atom, the shared selector resolves that wrong smid, and the reader lands on
  // a step they never selected. Same rule as the form branch of `useHPStepOperations`.
  const { selectStepWithIdentity } = useClinicalStepWriters();
  const setCurrentStepIndex = useCallback(
    (index: number) => selectStepWithIdentity({ index, stepSmid: null }),
    [selectStepWithIdentity]
  );
  // The *form* writer, not `applyProtocolLayouts`: while the form is open the case override is the
  // only layer `useLayouts` honours, and it is the layer the form's own layout picker writes. See
  // `applyFormStepLayouts` for why publishing to the protocol layer and clearing the override left a
  // Create-mode step showing the matched protocol's grid instead of its own (VX-4810).
  const { applyFormStepLayouts: applyViewerLayouts } = useViewerLayoutWriters();
  const { currentCaseId } = useCurrentCaseContext();

  /**
   * Reads hpFormStepConfigurations and hangingProtocolStepIndex imperatively via
   * useRecoilCallback snapshot so we always get the committed atom values at call
   * time, avoiding stale-closure issues that can occur when the init effect sets
   * the atom but the useCallback closure still captures the previous null value.
   */
  const saveCurrentStepFromForm = useRecoilCallback(
    ({ snapshot, set }) =>
      (): StepConfiguration[] | null => {
        const configs = snapshot.getLoadable(hpFormStepConfigurations).getValue();
        const stepIndex = snapshot.getLoadable(hangingProtocolStepIndex).getValue();
        // The registered interceptor can run before React renders a peer window's latest drop.
        // Read the form from the same snapshot as the steps, rather than a render-time closure.
        const formState = snapshot.getLoadable(formStateAtom).getValue();

        // Read from layoutsForAllCasesState (the source of truth for the viewer)
        // because the layout picker writes there, not to hangingProtocolLayoutsState.
        // Fall back to hangingProtocolLayoutsState if no case-specific layouts exist.
        const allCaseLayouts = snapshot.getLoadable(layoutsForAllCasesState).getValue();
        const caseLayouts = currentCaseId != null ? allCaseLayouts[currentCaseId] : null;
        const currentLayouts =
          caseLayouts ?? snapshot.getLoadable(hangingProtocolLayoutsState).getValue();

        if (configs == null) return null;

        const currentStep = configs[stepIndex];
        if (currentStep == null) return configs;

        // Only save form viewports that fall within the current layout bounds.
        // The form accumulates entries for every viewport grid position across
        // all windows, but each step should only own viewports that fit the
        // active layout. Without this filter, stale viewport data from one step
        // bleeds into another when switching steps.
        //
        // Use the viewer's currentLayouts (all open windows) as the source of
        // truth, not currentStep.layouts. The step's layout record may be stale
        // (e.g. DEFAULT_CREATE_HP_STEP only has window '0'), but the user may
        // have configured viewports on window '1' since then.
        const stepLayouts: Record<string, string> = {};
        if (currentLayouts) {
          const allConverted = layoutsToStepLayouts(currentLayouts);
          // Adopt layouts from ALL open viewer windows, not just the ones the
          // step already owns. This ensures window "1" layouts are saved when
          // the user configures a dual-monitor layout.
          for (const [windowId, layout] of Object.entries(allConverted)) {
            stepLayouts[windowId] = layout;
          }
          // Preserve any step-owned windows that aren't in the viewer's current
          // layouts (defensive — shouldn't happen in practice).
          for (const windowId of Object.keys(currentStep.layouts)) {
            if (stepLayouts[windowId] == null) {
              stepLayouts[windowId] = currentStep.layouts[windowId];
            }
          }
        } else {
          Object.assign(stepLayouts, currentStep.layouts);
        }

        // Preserve criteria from the existing step configuration when the form
        // viewport has not supplied criteria — the form may not render a classifier
        // for every viewport (e.g. multi-window layouts). An explicit empty result
        // must replace the old criteria, just like a non-empty result does.
        //
        // The mirror group id is carried over for a related but distinct reason: the form has no
        // representation of it at all, so a flush would otherwise drop it from every step the user
        // merely navigated through, and the save would un-mirror viewports nobody touched. It is
        // recomputed at payload time from what each viewport actually holds — see
        // `applyMirrorGroupsToStepViewports` — so carrying a stale value here costs nothing for the
        // step being edited and is the only thing that preserves it for the ones that aren't.
        const viewportConfigs: Record<string, ViewportConfigurationEntry> = {};
        for (const [viewportId, formViewport] of Object.entries(formState.viewports)) {
          if (!isViewportInLayoutBounds(viewportId, stepLayouts)) continue;
          const converted = formViewportToStepViewport(formViewport);
          const existingViewport = currentStep.viewports[viewportId];
          if (
            formViewport.seriesClassificationCriteria == null &&
            formViewport.seriesCriteriaSourceText ===
              existingViewport?.series_criteria_source_text &&
            existingViewport?.criteria?.length
          ) {
            (converted as { criteria: typeof existingViewport.criteria }).criteria =
              existingViewport.criteria;
          }
          viewportConfigs[viewportId] =
            existingViewport?.viewport_mirror_group_id != null
              ? {
                  ...converted,
                  viewport_mirror_group_id: existingViewport.viewport_mirror_group_id,
                }
              : converted;
        }

        const updatedStep: StepConfiguration = {
          ...currentStep,
          viewports: viewportConfigs,
          layouts: stepLayouts,
        };

        const updatedConfigurations = configs.map((step, i) =>
          i === stepIndex ? updatedStep : step
        );

        set(hpFormStepConfigurations, updatedConfigurations);
        return updatedConfigurations;
      },
    [formStateAtom, currentCaseId]
  );

  /**
   * Loads a step's viewports and layouts into the form state and viewer atoms.
   * Accepts an optional `configs` override so callers can pass freshly-saved
   * configurations instead of relying on the (potentially stale) closure value.
   *
   * Re-bases the form's unsaved-changes snapshot onto the arriving step as it goes: see the
   * re-baseline block below for why nothing else can do it.
   */
  const loadStepIntoForm = useRecoilCallback(
    ({ snapshot, set }) =>
      (stepIndex: number, configs?: StepConfiguration[] | null) => {
        const effectiveConfigs = configs ?? stepConfigurations;
        if (effectiveConfigs == null) return;

        const step = effectiveConfigs[stepIndex];
        if (step == null) return;

        // Projected inside the updater so it can read the study description the form holds right
        // now. A seeded viewport's criteria signature has to name the study description its criteria
        // are taken to describe, or every current-study viewport opens as still-waiting.
        setFormState((prev) => ({
          ...prev,
          viewports: stepToFormViewports(step, prev.studyCriteriaSourceText ?? ''),
          // A step load is authoritative, but its empty slots look like init placeholders. Advancing the
          // version lets the merge tell it from a stale echo of this window's own viewports (VX-5300).
          initializationVersion: Math.max(Date.now(), (prev.initializationVersion ?? 0) + 1),
        }));

        // The form's `viewports` describe the ACTIVE step, so the baseline the dirty check compares
        // them against has to describe the same step. `initialHPFormStateSnapshot` is captured once,
        // at form init, from step 0 -- so after any navigation the check was comparing step N's
        // projection against step 0's and reporting the difference between two steps as an edit the
        // user made. Symptom: open an HP with more than one step, click through the steps, hit
        // Cancel, and get a discard prompt with nothing to discard (VX-4947).
        //
        // Re-projecting here, rather than diffing more cleverly at the check, is what makes it hold:
        // both sides of the comparison then come out of `stepToFormViewports` with the same inputs,
        // so a pristine navigation cannot manufacture a difference. Projecting the *baseline* step
        // (not the live one) is what keeps a real edit visible -- re-reading the live step would
        // adopt the user's own edits into the baseline and lose them on Cancel.
        //
        // A step with no baseline entry (one added this session, or an index shifted by a reorder)
        // leaves the snapshot alone: both are genuine changes that the step-list comparison in
        // `hasUnsavedChanges` already reports.
        const baselineSteps = snapshot.getLoadable(hpFormStepsBaseline).getValue();
        const baselineStep = baselineSteps?.[stepIndex];
        if (baselineStep != null) {
          set(initialHPFormStateSnapshot, (prev) => {
            if (prev == null) return prev;
            const snapshotState = prev as HangingProtocolFormState;
            return {
              ...snapshotState,
              viewports: stepToFormViewports(
                baselineStep,
                snapshotState.studyCriteriaSourceText ?? ''
              ),
            };
          });
        }

        // Update layout atoms so the viewer grid reflects the step's layout
        const newLayouts = stepLayoutsToLayouts(step.layouts);
        applyViewerLayouts(newLayouts);

        // NOTE: Do NOT write to dehydratedHangingProtocol here. The form overlay
        // reads viewport data from formState, not dehydratedHP. Writing step
        // viewport configs (which have null study/series/stack for criteria-based
        // HPs) would overwrite the resolved assignments from the initial HP match,
        // causing blank viewports when the form closes.
      },
    [stepConfigurations, setFormState, applyViewerLayouts]
  );

  const switchToStep = useCallback(
    (newIndex: number) => {
      if (newIndex === currentStepIndex) return;
      if (stepConfigurations == null) return;
      if (newIndex < 0 || newIndex >= stepConfigurations.length) return;

      // Save current step's form state and get the freshly-updated configurations
      const updatedConfigs = saveCurrentStepFromForm();

      // NOTE: Do NOT write to steppedHPConfigurations (viewer atom) here.
      // The form overlay handles rendering via formState, not the viewer atom.
      // Writing normalized form data to the viewer atom causes clinical dirty
      // indicators to appear on steps that weren't edited, and the data may
      // differ from the baseline due to viewport format normalization.

      // Update the step index
      setCurrentStepIndex(newIndex);

      // Load the new step's data using the freshly-saved configurations
      // to avoid reading stale data from the closure
      loadStepIntoForm(newIndex, updatedConfigs);
    },
    [
      currentStepIndex,
      stepConfigurations,
      saveCurrentStepFromForm,
      setCurrentStepIndex,
      loadStepIntoForm,
    ]
  );

  const flushCurrentStepToConfigurations = useCallback((): StepConfiguration[] | null => {
    return saveCurrentStepFromForm();
  }, [saveCurrentStepFromForm]);

  // Register interceptors so StepBar / keyboard shortcuts route through
  // form sync while the HP form is open, and addStep/deleteStep can flush
  // form state before cloning or snapshotting steps.
  useEffect(() => {
    stepSwitchInterceptorRef.current = switchToStep;
    formFlushInterceptorRef.current = flushCurrentStepToConfigurations;
    return () => {
      stepSwitchInterceptorRef.current = null;
      formFlushInterceptorRef.current = null;
    };
  }, [switchToStep, flushCurrentStepToConfigurations]);

  return {
    switchToStep,
    flushCurrentStepToConfigurations,
  };
};
